import type { OfferLeistungszeitraum } from '../../../types';
import { formatDateToDDMMYYYY, parseDDMMYYYYToYMD } from '../../../utils/date';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const KW_PATTERN = /\bKW\s*\d/i;
const YEAR_MIN = 2000;
const YEAR_MAX = 2100;
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

export type OfferLeistungszeitraumDateRange = {
  from: string;
  to?: string;
};

function isRealIsoDate(ymd: string): boolean {
  if (!ISO_DATE_RE.test(ymd)) return false;
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

function compareIsoDates(a: string, b: string): number {
  return a.localeCompare(b);
}

function asInt(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isInteger(raw)) return raw;
  if (typeof raw === 'string' && /^-?\d+$/.test(raw.trim())) return Number.parseInt(raw.trim(), 10);
  return null;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function ymdFromUtc(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** Monday 00:00 UTC of ISO week 1 (the week that contains 4 January). */
function isoWeek1MondayUtc(year: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dow = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (dow - 1));
  return monday;
}

export function isoWeekFromUtcDate(d: Date): { year: number; week: number } {
  const dow = d.getUTCDay() || 7;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() - (dow - 1));
  const thursday = new Date(monday);
  thursday.setUTCDate(monday.getUTCDate() + 3);
  const year = thursday.getUTCFullYear();
  const week1 = isoWeek1MondayUtc(year);
  const week = 1 + Math.round((monday.getTime() - week1.getTime()) / MS_PER_WEEK);
  return { year, week };
}

export function isoWeekFromYmd(ymd: string): { year: number; week: number } | null {
  if (!isRealIsoDate(ymd)) return null;
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  return isoWeekFromUtcDate(new Date(Date.UTC(y, m - 1, d)));
}

export function isoWeekNow(): { year: number; week: number } {
  const now = new Date();
  return isoWeekFromUtcDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

export function isoWeekCount(year: number): number {
  return isoWeekFromUtcDate(new Date(Date.UTC(year, 11, 28))).week;
}

export function isoWeekToDateRange(year: number, week: number): { from: string; to: string } | null {
  if (!Number.isInteger(year) || !Number.isInteger(week)) return null;
  if (year < YEAR_MIN || year > YEAR_MAX) return null;
  if (week < 1 || week > isoWeekCount(year)) return null;
  const monday = isoWeek1MondayUtc(year);
  monday.setUTCDate(monday.getUTCDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return { from: ymdFromUtc(monday), to: ymdFromUtc(sunday) };
}

export function isOfferWeekLeistungszeitraum(
  lz: OfferLeistungszeitraum,
): lz is Extract<OfferLeistungszeitraum, { kind: 'week' }> {
  return 'kind' in lz && lz.kind === 'week';
}

function parseWeekLeistungszeitraum(o: Record<string, unknown>): OfferLeistungszeitraum | null {
  const year = asInt(o.year);
  const week = asInt(o.week);
  if (year == null || week == null) return null;
  if (year < YEAR_MIN || year > YEAR_MAX) return null;
  if (week < 1 || week > isoWeekCount(year)) return null;

  const toYearRaw = o.toYear == null || o.toYear === '' ? year : asInt(o.toYear);
  const toWeekRaw = o.toWeek == null || o.toWeek === '' ? week : asInt(o.toWeek);
  if (toYearRaw == null || toWeekRaw == null) return null;
  if (toYearRaw < YEAR_MIN || toYearRaw > YEAR_MAX) return null;
  if (toWeekRaw < 1 || toWeekRaw > isoWeekCount(toYearRaw)) return null;

  const start = isoWeekToDateRange(year, week);
  const end = isoWeekToDateRange(toYearRaw, toWeekRaw);
  if (!start || !end || compareIsoDates(end.from, start.from) < 0) return null;
  if (toYearRaw === year && toWeekRaw === week) {
    return { kind: 'week', year, week };
  }
  if (toYearRaw === year) {
    return { kind: 'week', year, week, toWeek: toWeekRaw };
  }
  return { kind: 'week', year, week, toYear: toYearRaw, toWeek: toWeekRaw };
}

function parseDateLeistungszeitraum(o: Record<string, unknown>): OfferLeistungszeitraum | null {
  const fromRaw = o.from;
  if (typeof fromRaw !== 'string' || KW_PATTERN.test(fromRaw)) return null;
  const from = fromRaw.trim().slice(0, 10);
  if (!isRealIsoDate(from)) return null;

  const toRaw = o.to;
  if (toRaw == null || toRaw === '') {
    return { from };
  }
  if (typeof toRaw !== 'string' || KW_PATTERN.test(toRaw)) return null;
  const to = toRaw.trim().slice(0, 10);
  if (!isRealIsoDate(to)) return null;
  if (compareIsoDates(to, from) < 0) return null;
  if (to === from) return { from };
  return { from, to };
}

/** Dates `{ from, to? }` or `{ kind: 'week', year, week, … }`. Rejects KW strings. */
export function parseOfferLeistungszeitraum(raw: unknown): OfferLeistungszeitraum | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    if (!raw.trim() || KW_PATTERN.test(raw)) return null;
    return null;
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.kind === 'week') return parseWeekLeistungszeitraum(o);
  return parseDateLeistungszeitraum(o);
}

export function isOfferLeistungszeitraum(x: unknown): x is OfferLeistungszeitraum {
  return parseOfferLeistungszeitraum(x) !== null;
}

export function formatLeistungszeitraumDisplay(lz: OfferLeistungszeitraum | null | undefined): string {
  const parsed = parseOfferLeistungszeitraum(lz);
  if (!parsed) return '—';
  if (isOfferWeekLeistungszeitraum(parsed)) {
    const toYear = parsed.toYear ?? parsed.year;
    const toWeek = parsed.toWeek ?? parsed.week;
    if (toYear === parsed.year && toWeek === parsed.week) {
      return `KW ${parsed.week}/${parsed.year}`;
    }
    if (toYear === parsed.year) {
      return `KW ${parsed.week}-${toWeek}/${parsed.year}`;
    }
    return `KW ${parsed.week}/${parsed.year} - KW ${toWeek}/${toYear}`;
  }
  const fromLabel = formatDateToDDMMYYYY(parsed.from);
  if (!parsed.to || parsed.to === parsed.from) return fromLabel;
  return `${fromLabel} - ${formatDateToDDMMYYYY(parsed.to)}`;
}

export function leistungszeitraumToDateRange(
  lz: OfferLeistungszeitraum | null | undefined,
): OfferLeistungszeitraumDateRange | null {
  const parsed = parseOfferLeistungszeitraum(lz);
  if (!parsed) return null;
  if (isOfferWeekLeistungszeitraum(parsed)) {
    const start = isoWeekToDateRange(parsed.year, parsed.week);
    const end = isoWeekToDateRange(parsed.toYear ?? parsed.year, parsed.toWeek ?? parsed.week);
    if (!start || !end) return null;
    return { from: start.from, to: end.to };
  }
  if (!parsed.to || parsed.to === parsed.from) return { from: parsed.from };
  return { from: parsed.from, to: parsed.to };
}

export function normalizeOfferLeistungszeitraumForSave(
  lz: OfferLeistungszeitraum | null | undefined,
): OfferLeistungszeitraum | undefined {
  const parsed = parseOfferLeistungszeitraum(lz);
  if (!parsed) return undefined;
  return parsed;
}

/** YYYY-MM-DD or DD.MM.YYYY (also D.M.YYYY / TT.MM.JJ) → YYYY-MM-DD. */
export function parseIssueDateToYmd(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s) return null;
  const iso = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}/.test(s) && isRealIsoDate(iso)) return iso;
  const fromDe = parseDDMMYYYYToYMD(s);
  if (fromDe && isRealIsoDate(fromDe)) return fromDe;
  return null;
}

/**
 * Invoice Leistungszeitraum: stored value wins (even if previously hidden).
 * Otherwise fall back to the invoice issue date. Invalid issue dates yield null.
 */
export function resolveInvoiceLeistungszeitraum(opts: {
  leistungszeitraum?: unknown;
  issueDate?: unknown;
}): OfferLeistungszeitraum | null {
  const parsed = parseOfferLeistungszeitraum(opts.leistungszeitraum);
  if (parsed) return parsed;
  const ymd = parseIssueDateToYmd(opts.issueDate);
  if (!ymd) return null;
  return { from: ymd };
}

/** Merge a Leistungszeitraum into raw offer_document_draft JSON without dropping other keys. */
export function mergeLeistungszeitraumIntoDraftJson(
  draftRaw: unknown,
  lz: OfferLeistungszeitraum,
  opts?: { onlyIfEmpty?: boolean },
): { json: string; wrote: boolean } {
  let obj: Record<string, unknown> = {};
  if (typeof draftRaw === 'string' && draftRaw.trim()) {
    try {
      const parsed = JSON.parse(draftRaw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        obj = { ...(parsed as Record<string, unknown>) };
      }
    } catch {
      obj = {};
    }
  } else if (draftRaw && typeof draftRaw === 'object' && !Array.isArray(draftRaw)) {
    obj = { ...(draftRaw as Record<string, unknown>) };
  }
  const onlyIfEmpty = opts?.onlyIfEmpty !== false;
  if (onlyIfEmpty && parseOfferLeistungszeitraum(obj.leistungszeitraum)) {
    return { json: JSON.stringify(obj), wrote: false };
  }
  obj.leistungszeitraum = lz;
  return { json: JSON.stringify(obj), wrote: true };
}

/** Rechnung, Abschlagsrechnung, Schlussrechnung, Stornorechnung */
export const INVOICE_DOCUMENT_TYPE_LABELS = new Set([
  'Rechnung',
  'Abschlagsrechnung',
  'Schlussrechnung',
  'Stornorechnung',
  'Gutschrift',
]);

export function documentShowsLeistungszeitraum(documentTypeLabel: string): boolean {
  return INVOICE_DOCUMENT_TYPE_LABELS.has(documentTypeLabel.trim());
}

/**
 * Rechnungsarten, bei denen der Leistungszeitraum nicht ausgeblendet werden kann (§ 14 Abs. 4 Nr. 6 UStG).
 * Abschlagsrechnungen sind Anzahlungsrechnungen: Der Zeitraum steht per Default drauf, darf aber weg.
 */
export function documentLocksLeistungszeitraum(documentTypeLabel: string): boolean {
  const t = documentTypeLabel.trim();
  return documentShowsLeistungszeitraum(t) && t !== 'Abschlagsrechnung';
}

export function documentShowsLieferdatum(documentTypeLabel: string): boolean {
  return documentTypeLabel.trim() === 'Lieferschein';
}

export type OfferSecondaryMetaKind = 'leistungszeitraum' | 'lieferdatum';

export function documentSecondaryMetaKind(documentTypeLabel: string): OfferSecondaryMetaKind | null {
  const t = documentTypeLabel.trim();
  if (documentShowsLeistungszeitraum(t) || t === 'Arbeitsbericht') return 'leistungszeitraum';
  if (documentShowsLieferdatum(t)) return 'lieferdatum';
  return null;
}

export function secondaryMetaFieldLabel(kind: OfferSecondaryMetaKind): string {
  return kind === 'lieferdatum' ? 'Lieferdatum' : 'Leistungszeitraum';
}
